import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { Link } from 'react-aria-components';
import type { MyRecognition, SpotNomination } from '@myrewards/contracts';
import { meQuery } from '../api/session';
import { listSpotNominations } from '../api/spot';
import { photos } from '../assets';
import {
  ButtonLink,
  Card,
  EmptyState,
  formatCount,
  formatMoney,
  formatShortDate,
  StatusPill,
} from '../ui';
import s from './Home.module.css';

const VALUE_LABEL: Record<SpotNomination['value'], string> = {
  creativity: 'Creativity',
  integrity: 'Integrity',
  passion: 'Passion',
  responsiveness: 'Responsiveness',
  collaboration: 'Collaboration',
};

const STATUS: Record<
  SpotNomination['status'],
  { label: string; tone: 'neutral' | 'info' | 'done' }
> = {
  draft: { label: 'Draft', tone: 'neutral' },
  // APP-225: the nominator's one status once it's sent; the server sends them nothing else.
  sent: { label: 'Sent for review', tone: 'info' },
  awarded: { label: 'Awarded', tone: 'done' },
  not_awarded: { label: 'Not awarded', tone: 'neutral' },
};

interface Row {
  id: string;
  who: string;
  value: string | null;
  status: SpotNomination['status'];
  updatedAt: string;
}

/**
 * Under each nomination: its value and when it was saved (a draft) or sent ("Integrity · sent Oct
 * 13"). A sent one's date is the day it was sent, whatever has happened since (APP-225).
 */
function rowMeta(row: Row): string {
  const when = `${row.status === 'draft' ? 'saved' : 'sent'} ${formatShortDate(row.updatedAt)}`;
  return row.value === null
    ? `${when.charAt(0).toUpperCase()}${when.slice(1)}`
    : `${row.value} · ${when}`;
}

/** How many of the person's own nominations the card lists before "See all". */
const SHOWN = 3;

/**
 * Spot Award nominations, open to every employee (K22): the entry point and the person's own
 * nominations, or the designed empty state before the first one. The list is the person's own
 * Spot list (the My nominations tab's), so "No nominations yet" is only said while it's true
 * (APP-093).
 */
export function SpotCard({ spot }: { spot: MyRecognition['spot'] }): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  // Preview shows sample data only: it never reads the previewer's own nominations.
  const live = me.preview === null;
  const mine = useQuery({
    queryKey: ['spot', 'page', 'mine', 'home'],
    queryFn: ({ signal }) => listSpotNominations('mine', { page: 1, size: 25 }, signal),
    staleTime: 30_000,
    enabled: live && spot.nominations.length === 0,
  });
  const range = mine.data?.config.cashRange ?? spot.cashRange;
  const rows: Row[] =
    spot.nominations.length > 0
      ? spot.nominations.map((n) => ({
          id: n.id,
          who: n.nominee.name,
          value: VALUE_LABEL[n.value],
          status: n.status,
          updatedAt: n.updatedAt,
        }))
      : (mine.data?.items ?? []).map((n) => ({
          id: n.id,
          who: n.nominees.map((p) => p.name).join(', ') || 'No one chosen yet',
          value: n.value,
          status: n.status,
          updatedAt: n.updatedAt,
        }));
  const count = spot.nominations.length > 0 ? spot.nominations.length : (mine.data?.count ?? 0);
  return (
    <Card
      title="Saw great work lately?"
      titleSize="md"
      compact
      media={{ src: photos.womanEngineerTestingElectronics, alt: '' }}
      badge={<StatusPill tone="waiting" size="sm" label="Anyone can nominate" />}
      help={{
        shows: `Spot Award nominations: anyone at Areté can nominate a colleague for work that stood out${range ? `, for ${formatMoney(range.minCents)}–${formatMoney(range.maxCents)} in cash` : ''}. Your own nominations are listed here: drafts to finish, and the ones you sent for review.`,
        canDo: 'Nominate someone, or open a nomination to finish a draft or read what you sent.',
      }}
    >
      <p className={s.cardText}>
        Say who, a short title and what they did. It takes about two minutes, and the Spot Committee
        reads every one.
      </p>
      <ButtonLink href="/recognize/spot" variant="gold" block>
        Nominate for a Spot Award
      </ButtonLink>
      <h3 className={s.subhead}>Your nominations</h3>
      {rows.length === 0 && live && mine.isPending ? (
        <p className={s.meta}>Loading your nominations…</p>
      ) : rows.length === 0 && mine.isError ? (
        <p className={s.meta}>
          Your nominations are on the{' '}
          <Link href="/recognize/spot?tab=mine" className={s.strongLink}>
            Spot nominations page
          </Link>
          .
        </p>
      ) : rows.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="award"
          size="sm"
          headingLevel={4}
          headline="No nominations yet"
          body="When you send or save one, it shows here."
        />
      ) : (
        <>
          <ul className={s.plainList}>
            {rows.slice(0, SHOWN).map((row) => (
              <li key={row.id} className={s.nomination}>
                <span className={s.nominationText}>
                  <Link href={`/recognize/spot/${row.id}`} className={s.strongLink}>
                    {row.who}
                  </Link>
                  <span className={s.meta}>{rowMeta(row)}</span>
                </span>
                <StatusPill
                  tone={STATUS[row.status].tone}
                  size="sm"
                  label={STATUS[row.status].label}
                />
              </li>
            ))}
          </ul>
          {count > SHOWN && (
            <Link href="/recognize/spot?tab=mine" className={s.strongLink}>
              {`See all ${formatCount(count)} nominations`}
            </Link>
          )}
        </>
      )}
    </Card>
  );
}
